import {
  Box,
  Stack,
  Typography,
  Chip,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import DatasetTypeChip from "../components/DatasetTypeChip";

import { SourceDataRecordsApiResponse } from "./DatasetViewer";
import DatasetInfoItems from "./DatasetInfoItems";
import { Dataset } from "../types";

type DatasetViewerHeaderProps = {
  dataset: Dataset;
  sourceDataRecords: SourceDataRecordsApiResponse | undefined;
};

const DatasetViewerHeader = ({
  dataset,
  sourceDataRecords,
}: DatasetViewerHeaderProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));
  const isSm = useMediaQuery(theme.breakpoints.only("sm"));
  const isMd = useMediaQuery(theme.breakpoints.only("md"));
  const isLg = useMediaQuery(theme.breakpoints.only("lg"));
  const isXl = useMediaQuery(theme.breakpoints.only("xl"));

  const getCharacterLimit = () => {
    if (isXs) return 18;
    if (isSm) return 28;
    if (isMd) return 40;
    if (isLg) return 45;
    if (isXl) return 50;
    return 80;
  };

  const characterLimit = getCharacterLimit();
  return (
    <Box data-testid="sdr-header" sx={{ py: 1, px: isMobile ? 1 : 2 }}>
      {/* Title row with dataset type icon, name, and optional remote badge */}
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        sx={{ flexWrap: "nowrap", mb: 0.5 }}
      >
        <DatasetTypeChip
          type={dataset.dataset_type?.name ?? ""}
          fontSize={isMobile ? 14 : 20}
          avatar
        />
        <Tooltip title={dataset?.name} placement="top" arrow>
          <Typography
            data-testid="sdr-title"
            component="h1"
            variant={isMobile ? "subtitle1" : "h5"}
            fontSize={isMobile ? 14 : 20}
            fontWeight={600}
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {dataset?.name.length > characterLimit
              ? `${dataset.name.substring(0, characterLimit)}...`
              : dataset?.name}
          </Typography>
        </Tooltip>
        {dataset.remote_dataset && (
          <Chip
            label="Remote"
            size="small"
            sx={{ height: 22, fontSize: "0.7rem" }}
          />
        )}
      </Stack>

      {/* Info row: row count, created by, date */}
      {!isMobile && (
        <DatasetInfoItems
          dataset={dataset}
          sourceDataRecords={sourceDataRecords}
        />
      )}
    </Box>
  );
};

export default DatasetViewerHeader;
